import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AuthenticationService } from './authentication.service';

/**
 * Resolves an S3 key to a fully-formed file URL ready for an <img src> or
 * <a href>. Operates in three explicit modes depending on environment.S3URL
 * and JWT presence (see "FileTokenService 3-mode" in the S3 privatization
 * plan):
 *
 *  (1) Passthrough  — environment.S3URL still equals the legacy public S3
 *      host (Stage A default). No mint, no /tokens call; emit `S3URL + key`
 *      directly. Identical to today's behavior, so Stage A is a true no-op.
 *
 *  (2) Authenticated mint — environment.S3URL points at the upload-MS proxy
 *      AND a user JWT is present in localStorage. Batches the key into a
 *      microtask-queued POST /api/v1/files/tokens call, returns
 *      `${S3URL}${key}?t=<jwt>`. Token cached in memory until expiry.
 *
 *  (3) Anonymous fallback — environment.S3URL points at the proxy AND no
 *      JWT is available (anonymous SPA surfaces like submit-public-rfq).
 *      Emits the legacy direct-S3 URL — still works in Stages A and B
 *      because the bucket is public. Stage C regression accepted by design
 *      for unauthenticated content.
 *
 * Network calls go through Angular's HttpClient so the existing TokenInterceptor
 * picks them up. The batch is fired once per microtask, so a page rendering
 * 20+ images results in one /tokens round-trip, not 20.
 */

const LEGACY_S3_HOST_PATTERN = /^https?:\/\/(?:teradix-general\.s3\.amazonaws\.com\/|s3\.amazonaws\.com\/teradix-general\/)/i;
const LEGACY_S3_URL = 'https://teradix-general.s3.amazonaws.com/';
const CACHE_REFRESH_BUFFER_SECONDS = 30;

interface CachedToken {
  token: string;
  exp: number; // unix seconds
}

interface TokensResponse {
  tokens: { [key: string]: string };
}

@Injectable({ providedIn: 'root' })
export class FileTokenService {
  private cache = new Map<string, CachedToken>();
  private pendingKeys = new Set<string>();
  private pendingSubject: ReplaySubject<{ [key: string]: string }> | null = null;
  private batchScheduled = false;

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
  ) {}

  /**
   * Resolve a key to its full URL. The Observable emits exactly once and
   * completes. Empty/falsy keys resolve to ''. Errors during mint fall back
   * to the legacy direct S3 URL so a page never goes blank.
   */
  urlFor(key: string | null | undefined): Observable<string> {
    if (!key) return of('');

    const s3Url = environment.S3URL;

    // Mode (1): passthrough — env still points at legacy S3 host.
    if (LEGACY_S3_HOST_PATTERN.test(s3Url)) {
      return of(s3Url + key);
    }

    // Mode (3): anonymous fallback — no JWT, emit legacy direct S3 URL.
    if (!this.auth.getToken()) {
      return of(LEGACY_S3_URL + key);
    }

    // Mode (2): authenticated mint. Cache hit?
    const cached = this.cache.get(key);
    const nowSec = Math.floor(Date.now() / 1000);
    if (cached && cached.exp > nowSec + CACHE_REFRESH_BUFFER_SECONDS) {
      return of(`${s3Url}${key}?t=${cached.token}`);
    }

    return this.mintToken(key).pipe(
      map(token => `${s3Url}${key}?t=${token}`),
      catchError(() => of(LEGACY_S3_URL + key)),
    );
  }

  /**
   * Queue this key into the current microtask batch. Multiple urlFor() calls
   * in the same tick share a single /tokens request and ReplaySubject so all
   * subscribers — including ones that subscribe after the response lands —
   * receive their per-key token.
   */
  private mintToken(key: string): Observable<string> {
    this.pendingKeys.add(key);
    if (!this.pendingSubject) {
      this.pendingSubject = new ReplaySubject<{ [key: string]: string }>(1);
    }
    const subjectForThisBatch = this.pendingSubject;

    if (!this.batchScheduled) {
      this.batchScheduled = true;
      Promise.resolve().then(() => this.flush());
    }

    return subjectForThisBatch.pipe(
      map(tokens => {
        const t = tokens[key];
        if (!t) throw new Error(`token missing for key=${key}`);
        return t;
      }),
    );
  }

  private flush(): void {
    const keys = Array.from(this.pendingKeys);
    const subject = this.pendingSubject;
    this.pendingKeys.clear();
    this.pendingSubject = null;
    this.batchScheduled = false;

    if (!keys.length || !subject) return;

    // TokenInterceptor (src/app/shared/services/http-interceptor.service.ts)
    // adds the Authorization header from localStorage('token') on every request.
    // URL stays absolute — adminui services consistently build full URLs from
    // env vars (no API-prefix interceptor exists in this repo, unlike userui).
    const tokensUrl = this.deriveTokensEndpoint(environment.S3URL);
    this.http.post<TokensResponse>(tokensUrl, { keys }).subscribe(
      response => {
        const tokens = (response && response.tokens) || {};
        this.cacheBatch(tokens);
        subject.next(tokens);
        subject.complete();
      },
      err => {
        // Surface for ops, then propagate so callers fall back to legacy URL.
        // tslint:disable-next-line: no-console
        console.warn('[FileTokenService] batch mint failed:', err && err.message);
        subject.error(err);
      },
    );
  }

  /**
   * environment.S3URL ends with `/api/v1/files/` in Stage B. The /tokens
   * endpoint sits at `/api/v1/files/tokens` on the same host. Strip trailing
   * slash, append `tokens`.
   */
  private deriveTokensEndpoint(s3Url: string): string {
    const noTrailing = s3Url.replace(/\/+$/, '');
    return noTrailing + '/tokens';
  }

  private cacheBatch(tokens: { [key: string]: string }): void {
    Object.keys(tokens).forEach(k => {
      const exp = this.decodeExp(tokens[k]);
      if (exp !== null) {
        this.cache.set(k, { token: tokens[k], exp });
      }
    });
  }

  /**
   * Decode the JWT payload to extract the `e` (exp unix seconds) claim. Used
   * for client-side cache TTL; the proxy is the authority for actual validity.
   * Returns null if the token is malformed.
   */
  private decodeExp(jwt: string): number | null {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return null;
      // base64url → base64 (replace -/_, pad to multiple of 4)
      let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4 !== 0) b64 += '=';
      const payload = JSON.parse(atob(b64));
      return typeof payload.e === 'number' ? payload.e : null;
    } catch (e) {
      return null;
    }
  }
}
